import { CREATURE_SIZE_TYPES } from '../../shared/creature-size-types';
import { firstSubtypeFacts, subtypeFacts } from './subtype-facts';
import { createClassificationContext } from '../compile/from-base-classification-context';
import { readItemField } from '../compile/item-field-reader';
import type { FromBaseInventoryMetadata } from '../compile/from-base-inventory';
import type { FromBaseRow } from '../compile/from-base-context';
import { authoringReaders, enchantmentPartEligible, PART_SLOTS, type AuthoringFacts, type Selections } from './enchantment';
import { computeRequiredItemCharges, type RequiredChargeDeps } from '../port/k1-compile/db/enchantment_templates/compute_required_item_charges';
import type { PortEnv } from '../port-contract';
import { compileFetchedEntity } from '../../shared/compile-fetched-entity';
import { primaryPictureOf } from '../../shared/array-types';
import { readLayerListEntryUuid } from '../compile/inventory-raw-readers';
import { decodeHtmlEntitiesForPlainText } from '../../shared/decode-html-entities';
import { SHAPES } from '../ability/aoe/expand-placed-aoe-targets';
import type { MagicArea } from '../../shared/magic-area';
import { fromBaseRecord, fromBaseUuid } from '../compile/from-base-context';
import { constructInventoryReferenceShell } from '../compile/from-base-inventory-references';

export const CREATURE_MODEL_GENDER_AGE = {
    man: { gender_variable: 'male', weight_mult: 1, size_step: 0, default_size_variable: 'medium' },
    woman: { gender_variable: 'female', weight_mult: .85, size_step: 0, default_size_variable: 'medium' },
    boy: { gender_variable: 'male', weight_mult: .5, size_step: -1, default_size_variable: 'small' },
    girl: { gender_variable: 'female', weight_mult: .45, size_step: -1, default_size_variable: 'small' },
} as const;
export const GENDER_UUID_BY_VARIABLE: Readonly<Record<string, string>> = {
    male: '0D629F19-23AC-18BE-B79C-73CB52703F66', female: '1214C099-5021-24B5-5D8D-608B832CE70A',
    unspecified_gender: '338C4063-2F1E-6A9D-5451-8E6ED3266353',
};
export const FALLBACK_CREATURE_TYPE_HUMANOID = '3A28EC64-DA47-09A0-AD1B-31B32627094F';

export function ftParseCreatureModelTokens(raw: string): { subTypeTokens: string[]; ageSex: keyof typeof CREATURE_MODEL_GENDER_AGE | null } {
    const body = raw.slice(raw.indexOf(':') + 1);
    const parts = body.split(',').map(part => part.trim()).filter(Boolean);
    const last = parts[parts.length - 1]?.toLowerCase() ?? '';
    const ageSex = Object.prototype.hasOwnProperty.call(CREATURE_MODEL_GENDER_AGE, last) ? last as keyof typeof CREATURE_MODEL_GENDER_AGE : null;
    return { subTypeTokens: ageSex ? parts.slice(0, -1) : parts, ageSex };
}

export function ftSizeStepFromUuidOrVariableName(input: string, delta: number): string {
    let index = CREATURE_SIZE_TYPES.findIndex(row => row.item_id === input || row.variable_name === input);
    if (index < 0) index = 2;
    return CREATURE_SIZE_TYPES[Math.min(CREATURE_SIZE_TYPES.length - 1, Math.max(0, index + delta))]!.item_id;
}
export function ftDefaultCreatureAutofillWeightLbFromSize(input: string): number {
    const size = CREATURE_SIZE_TYPES.find(row => row.item_id === input || row.variable_name === input) ?? CREATURE_SIZE_TYPES[2]!;
    return Math.round(180 * size.weight_multiplier / 10);
}

/** Exact matches across singular/plural terms precede every prefix/substring match. */
export function modelAncestryTerms(token: string): string[] {
    const plural = token.replace(/(\w+)$/, word => /(?:f|fe)$/i.test(word) ? word.replace(/(?:f|fe)$/i, 'ves')
        : /(?:s|x|z|ch|sh)$/i.test(word) ? word + 'es' : /[^aeiou]y$/i.test(word) ? word.slice(0, -1) + 'ies' : word + 's');
    return [...new Set([token, plural].filter(Boolean))];
}
export function modelAncestryIds(name: string, candidates: readonly FromBaseRow[]): string[] {
    return [...new Set(ftParseCreatureModelTokens(name).subTypeTokens.flatMap(token => {
        const terms = modelAncestryTerms(token).map(term => term.toLowerCase());
        for (const matches of [(value: string, term: string) => value === term,
            (value: string, term: string) => value.startsWith(term), (value: string, term: string) => value.includes(term)]) {
            for (const term of terms) {
                const row = candidates.find(row => matches(String(readItemField(row, 'item_name') ?? '').toLowerCase(), term));
                if (row) return [String(readItemField(row, 'item_id'))];
            }
        }
        throw new Error(`Model ancestry unavailable: ${token}`);
    }))];
}

export function creatureModelLayers(name: string, facts: readonly ReturnType<typeof subtypeFacts>[]): FromBaseRow {
    const { ageSex } = ftParseCreatureModelTokens(name);
    const age = ageSex ? CREATURE_MODEL_GENDER_AGE[ageSex]
        : { gender_variable: 'unspecified_gender', weight_mult: 1, size_step: 0, default_size_variable: 'medium' };
    const first = firstSubtypeFacts(facts);
    const size = ftSizeStepFromUuidOrVariableName(first.size || age.default_size_variable, age.size_step);
    return { creature_sub_type: facts.map(row => row.id), creature_type: first.creatureType || FALLBACK_CREATURE_TYPE_HUMANOID,
        gender: GENDER_UUID_BY_VARIABLE[age.gender_variable], size,
        weight: Math.round((Number.isFinite(first.weight) ? first.weight : ftDefaultCreatureAutofillWeightLbFromSize(size)) * age.weight_mult) };
}

/** CLI and NPC authoring use the existing native classification and subtype readers. */
export function planModelFacts(name: string, base: FromBaseRow, metadata: FromBaseInventoryMetadata,
    candidates: readonly FromBaseRow[], authored: FromBaseRow = {}): FromBaseRow {
    const ids = modelAncestryIds(name, candidates), context = createClassificationContext(base, metadata);
    const types = ['creature_ancestry', 'creature_grouping', 'creature_lineage', 'creature_sub_type', 'creature_sub_type_ancestral'];
    const facts = ids.map(id => {
        const row = context.definition(id);
        if (!row) throw new Error(`Model ancestry definition unavailable: ${id}`);
        if (!types.includes(String(readItemField(row, 'item_type')))) throw new Error(`Model ancestry has the wrong kind: ${id}`);
        return subtypeFacts(id, row, types, { item: (row, key) => readItemField(row as FromBaseRow, key),
            layer: (row, key) => context.readers.layerValue(row as FromBaseRow, key) });
    });
    const result = { ...authored };
    for (const [key, value] of Object.entries(creatureModelLayers(name, facts)))
        if (result[key] === undefined || result[key] === null || result[key] === '' || Array.isArray(result[key]) && !result[key].length) result[key] = value;
    return result;
}

/** Older catalog responses remain usable while the additive SELECT package is being delivered. */
export async function loadModelFacts(name: string, candidates: readonly FromBaseRow[]): Promise<FromBaseRow> {
    const ids = modelAncestryIds(name, candidates);
    const facts = await Promise.all(ids.map(async id => {
        const response = await fetch(`${BASE_URL}options/api/authored_card_inputs_inc.php?id=${encodeURIComponent(id)}`);
        if (!response.ok) throw new Error('Model authoring inputs unavailable. Please retry.');
        const row = await compileFetchedEntity(await response.json());
        if (String(readItemField(row, 'item_id')).toUpperCase() !== id.toUpperCase()) throw new Error('Model ancestry identity unavailable');
        return subtypeFacts(id, row, ['creature_ancestry', 'creature_grouping', 'creature_lineage', 'creature_sub_type', 'creature_sub_type_ancestral']);
    }));
    return creatureModelLayers(name, facts);
}

/** The CLI charge floor invokes the same native export used by the enchantment form. */
export function requiredChargeFacts(selections: Selections, facts: AuthoringFacts): number {
    const read = authoringReaders(facts);
    const deps: RequiredChargeDeps = {
        valid_uuid: id => { if (!read.definition(String(id))) throw new Error(`Charge definition unavailable: ${String(id)}`); },
        query_full_item_arr: id => read.definition(String(id)),
        layer_value: (row, key) => read.layerValue(row as FromBaseRow, key),
    };
    return Number(computeRequiredItemCharges(selections.charged_ability ?? [], selections.charged_spell ?? [],
        { deps } as unknown as PortEnv<RequiredChargeDeps>));
}

export function validateDefinitionParts(selections: Selections, facts: AuthoringFacts): void {
    const read = authoringReaders(facts);
    for (const slot of PART_SLOTS) for (const id of selections[slot] ?? []) {
        const part = read.definition(id);
        if (!part) throw new Error(`Enchantment definition unavailable: ${id}`);
        if (!enchantmentPartEligible(part, read)) throw new Error(`${read.name(part)} cannot be an enchantment part.`);
        const spell = read.itemField(part, 'item_type') === 'spell';
        if (slot.includes('spell') !== spell) throw new Error(`${read.name(part)} belongs in ${spell ? 'a spell' : 'an ability'} slot.`);
    }
}

export type DefinitionFacts = { base: FromBaseRow; metadata: FromBaseInventoryMetadata };
export type RuneFieldSchema = { scalars: string[]; multi: string[]; single: string[]; images: string[]; nested: string[];
    visual: Record<string, { reference: boolean; multiple: boolean }> };

/** Art consumes the same native provider walk as authoring, over saved definitions and media. */
export function runeRowsFromFacts(facts: DefinitionFacts, ids: readonly string[], schema: RuneFieldSchema): FromBaseRow[] {
    const context = createClassificationContext(facts.base, facts.metadata), { readers, definition } = context;
    const name = (row: FromBaseRow) => decodeHtmlEntitiesForPlainText(readItemField(row, 'item_name'));
    const refs = (row: FromBaseRow, key: string, images = false, nested = false): FromBaseRow[] =>
        Object.values(readers.layerEntries(row, key)).flatMap(entry => {
            const id = readLayerListEntryUuid(entry);
            const held = fromBaseRecord(facts.base.catalog) && facts.base.catalog[id.toUpperCase()];
            const type = facts.metadata.layerCatalog[key]?.references_item_type;
            const child = !fromBaseUuid(id) ? null : held ? definition(id)
                : type ? constructInventoryReferenceShell(id.toUpperCase(), type, facts.metadata, () => null) : null;
            const label = child ? name(child) : typeof entry === 'string' ? entry : readItemField(entry as FromBaseRow, 'item_name');
            if (!label) return [];
            return [{ items: { item_name: label }, ...(images ? { pic_url: child ? readItemField(primaryPictureOf(child), 'picture_url') : null } : {}),
                ...(nested && child ? { item_layer_types: { ...Object.fromEntries(schema.nested.flatMap(key => {
                    const value = readers.layerValue(child, key);
                    return value == null || value === '' ? [] : [[key, String(value)]];
                })), damage_type: refs(child, 'damage_type') } } : {}) }];
        });
    return ids.map(id => {
        const row = definition(id);
        if (!row) throw new Error(`Rune definition unavailable: ${id}`);
        const layers: FromBaseRow = {};
        const keys = new Set([...schema.scalars, ...schema.multi, ...schema.single, ...schema.images,
            ...Object.keys(schema.visual), ...Object.keys(facts.metadata.layerCatalog)]);
        for (const key of keys) {
            const status = /^status_effect_(?:applied_|removed_)/.test(key);
            const summon = /^user_choice_\d+_summon$/.test(key), shape = /^user_choice_\d+_shapeshift$/.test(key);
            const visual = schema.visual[key];
            const reference = status || summon || shape || schema.images.includes(key) || schema.multi.includes(key)
                || schema.single.includes(key) || visual?.reference;
            if (reference) {
                const entries = refs(row, key, summon || schema.images.includes(key), status);
                if (entries.length) layers[key] = schema.single.includes(key) || visual && !visual.multiple && !status && !shape && !summon
                    && !schema.multi.includes(key) && !schema.images.includes(key) ? entries[0] : entries;
            } else if (schema.scalars.includes(key) || visual) {
                const value = readers.layerValue(row, key);
                if (value != null && value !== '') layers[key] = String(value);
            }
        }
        return { items: { item_id: readItemField(row, 'item_id'), item_name: name(row), item_type: readItemField(row, 'item_type') },
            item_layer_types: layers };
    });
}

/** Fixed gallery placement, with dimensions read through the native definition provider walk. */
export function reviewAreaFromFacts(facts: DefinitionFacts, id: string): MagicArea | null {
    const context = createClassificationContext(facts.base, facts.metadata), row = context.definition(id);
    if (!row) throw new Error(`Review definition unavailable: ${id}`);
    const value = (key: string) => context.readers.layerValue(row, key);
    const type = String(value('aoe_type'));
    const shape = SHAPES[type.toUpperCase()] ?? Object.values(SHAPES).find(shape => shape === type.toLowerCase());
    const distance = Number(value('aoe_distance') ?? 0);
    if (!shape || !Number.isFinite(distance) || distance <= 0) return null;
    const depth = shape === 'cube' ? distance : Number(value('aoe_distance_depth') ?? 1);
    const height = shape === 'cube' ? distance : Number(value('aoe_distance_height') ?? (shape === 'cylinder' ? 0 : 1));
    if (!Number.isFinite(depth) || depth <= 0 || !Number.isFinite(height) || height < 0 || height === 0 && shape !== 'cylinder') return null;
    return { schema: 'ft.vfx.area.v1', encounterId: 'C0DEC0DE-CE11-4000-ABCD-000000000003', shape: shape as MagicArea['shape'],
        origin: { x: 0, y: 0, z: 0 }, end: { x: distance, y: 0, z: 0 }, distance, depth, height };
}
